"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseArc = parseArc;
const Util_1 = require("../../Util");
const v6Common_1 = require("./v6Common");
const Utils_1 = require("@cadstrom/sigma-engine-shared/dist/Utils");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
function parseArc(isModule, node, _isFootprint, warnings, defaultLayerName, layerSelector, minLineWidth = 0, netMap = []) {
    let start = [0, 0];
    let mid;
    let end = [0, 0];
    let lineWidth = 0;
    let lineStyle;
    let angleDeg;
    let layerName = defaultLayerName;
    let net;
    const flipY = isModule ? -1 : 1;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'start': {
                start = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
                break;
            }
            case 'mid': {
                mid = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
                break;
            }
            case 'end': {
                end = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
                break;
            }
            case 'width': {
                lineWidth = parseFloat(n.values[0]);
                break;
            }
            case 'angle': {
                angleDeg = parseFloat(n.values[0]);
                break;
            }
            case 'layer':
                layerName = (0, Util_1.removeQuotes)(n.values[0]);
                break;
            case 'stroke': {
                const stroke = (0, v6Common_1.parseStrokeDirective)(n);
                lineWidth = stroke.width;
                lineStyle = stroke.type;
                break;
            }
            case 'net': {
                const netId = n.values[0];
                const netName = netMap.find((n) => n.id === netId);
                if (netName) {
                    net = netName.value;
                }
            }
        }
    });
    if (!layerName) {
        warnings.push(`Arc at [${start[0]}, ${start[1]}] => [${end[0]}, ${end[1]}] did not have layer specified`);
        return undefined;
    }
    const layer = layerSelector.filterLayers(layerName)[0];
    if (!layer) {
        warnings.push(`Arc at [${start[0]}, ${start[1]}] => [${end[0]}, ${end[1]}] used unknown layer ${layerName}`);
        return undefined;
    }
    let radius, center;
    if (typeof angleDeg === 'number') {
        const angleRad = (0, Utils_1.degreesToRadians)(angleDeg);
        radius = Math.sqrt(Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2));
        center = [
            start[0],
            start[1],
        ];
        if (center[0] <= end[0] && center[1] > end[1]) {
            start = [center[0] + radius * Math.sin(angleRad), center[1] + radius * Math.cos(angleRad)];
        }
        else if (center[0] < end[0] && center[1] <= end[1]) {
            start = [center[0] - radius * Math.cos(angleRad), center[1] + radius * Math.sin(angleRad)];
        }
        else if (center[0] >= end[0] - Number.EPSILON && center[1] < end[1]) {
            start = [center[0] - radius * Math.sin(angleRad), center[1] + radius * Math.cos(angleRad)];
        }
        else {
            start = [center[0] - radius * Math.cos(angleRad), center[1] - radius * Math.sin(angleRad)];
        }
    }
    else if (mid) {
        const found = findCircleFromPoints(start, mid, end);
        radius = found.radius;
        center = found.center;
    }
    else {
        warnings.push(`Unable to compute arc at [${start[0]}, ${start[1]}] => [${end[0]}, ${end[1]}]`);
        return undefined;
    }
    let startAngle = angleOfPoint(start, center, radius);
    let endAngle = angleOfPoint(end, center, radius);
    if (typeof angleDeg !== 'undefined') {
        if (angleDeg >= 0) {
            startAngle = endAngle - angleDeg;
        }
        else {
            const temp = startAngle;
            startAngle = endAngle;
            endAngle = temp;
        }
    }
    else if (endAngle > startAngle) {
        if (mid) {
            const midAngle = angleOfPoint(mid, center, radius);
            if (midAngle < startAngle) {
                const temp = startAngle;
                startAngle = endAngle;
                endAngle = temp;
            }
            else {
                startAngle = (0, Transform_1.clampRotation)(startAngle + 360);
            }
        }
        else {
            const temp = startAngle;
            startAngle = endAngle;
            endAngle = temp;
        }
    }
    const arc = {
        type: 'arc',
        center: [(0, Transform_1.roundToMaxOutputPositionPrecision)(center[0]), (0, Transform_1.roundToMaxOutputPositionPrecision)(center[1])],
        startAngle: (0, Transform_1.roundToMaxOutputAnglePrecision)(360 - startAngle),
        endAngle: (0, Transform_1.roundToMaxOutputAnglePrecision)(360 - endAngle),
        radius: (0, Transform_1.roundToMaxOutputPositionPrecision)(radius),
        lineWidth: (0, Utils_1.max)(lineWidth, minLineWidth),
        net,
    };
    if (mid) {
        const midAngle = angleOfPoint(mid, center, radius);
        const midAngleOut = 360 - midAngle;
        const normalize = (angle) => ((angle % 360) + 360) % 360;
        const span = normalize(arc.endAngle - arc.startAngle);
        const midOffset = normalize(midAngleOut - arc.startAngle);
        if (midOffset > span) {
            const temp = arc.startAngle;
            arc.startAngle = arc.endAngle;
            arc.endAngle = temp;
        }
    }
    if (arc.endAngle === 0) {
        arc.endAngle += 360;
    }
    if (lineStyle) {
        arc.lineStyle = lineStyle;
    }
    return {
        layers: [layer.id],
        drawing: arc,
    };
}
function angleOfPoint(point, center, radius) {
    const cosine = Math.min(1, Math.max(-1, (0, Transform_1.roundToMultiple)((point[0] - center[0]) / radius, 0.0001)));
    let angle = (0, Utils_1.radiansToDegrees)(Math.acos(cosine));
    if (point[1] - center[1] > Number.EPSILON) {
        angle = 360 - angle;
    }
    else if (Number.isNaN(angle)) {
        angle = 0;
    }
    return angle;
}
function findCircleFromPoints(start, mid, end) {
    const x12 = start[0] - mid[0];
    const x13 = start[0] - end[0];
    const y12 = start[1] - mid[1];
    const y13 = start[1] - end[1];
    const y31 = end[1] - start[1];
    const y21 = mid[1] - start[1];
    const x31 = end[0] - start[0];
    const x21 = mid[0] - start[0];
    const sx13 = Math.pow(start[0], 2) - Math.pow(end[0], 2);
    const sy13 = Math.pow(start[1], 2) - Math.pow(end[1], 2);
    const sx21 = Math.pow(mid[0], 2) - Math.pow(start[0], 2);
    const sy21 = Math.pow(mid[1], 2) - Math.pow(start[1], 2);
    const f = (sx13 * x12 + sy13 * x12 + sx21 * x13 + sy21 * x13) / (2 * (y31 * x12 - y21 * x13));
    const g = (sx13 * y12 + sy13 * y12 + sx21 * y13 + sy21 * y13) / (2 * (x31 * y12 - x21 * y13));
    const c = -Math.pow(start[0], 2) - Math.pow(start[1], 2) - 2 * g * start[0] - 2 * f * start[1];
    const h = -g;
    const k = -f;
    const sqr_of_r = h * h + k * k - c;
    const r = Math.sqrt(sqr_of_r);
    return {
        center: [(0, Transform_1.roundToMaxOutputPositionPrecision)(h), (0, Transform_1.roundToMaxOutputPositionPrecision)(k)],
        radius: (0, Transform_1.roundToMaxOutputPositionPrecision)(r),
    };
}
//# sourceMappingURL=v6ParseArc.js.map